import './PromoLinksList.sass';

import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import copyToClipboard from 'copy-text-to-clipboard';

import { ActionBottomSheet, Button, Header, Placeholder, ScrollView } from '$uikit';
import { Loader } from '$shared/components';
import { promoLinksActions, promoLinksSelector } from '$store/promoLinks';
import { useTranslator } from '$hooks';
import { IcCopy28, IcEdit28, IcLink28, IcPlusCircle28 } from '$assets';
import { _confirm } from '$utils';
import { toastActions } from '$store/toast';
import { showModal } from '$navigation/helper';
import { prettifyNumber } from '$utils/number';

export const PromoLinksList: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { links, isLinksLoading } = useSelector(promoLinksSelector);
  const [currentLinkId, setCurrentLinkId] = useState(0);

  useEffect(() => {
    dispatch(promoLinksActions.loadLinks());
  }, []);

  const currentLink = links.find((link) => link.id === currentLinkId);

  const handleCopy = useCallback(
    (url: string) => {
      copyToClipboard(url);
      dispatch(toastActions.setToastSuccess(t('copied')));
    },
    [dispatch, t],
  );

  const handleActionChange = (value: string) => {
    if (!currentLink) {
      return;
    }

    if (value === 'copy') {
      handleCopy(currentLink.webUrl);
    } else if (value === 'copy_mini_app') {
      handleCopy(currentLink.miniAppUrl);
    } else if (value === 'edit') {
      dispatch(promoLinksActions.startEdit(currentLink));
      showModal('create_promo_link', false, { editLinkId: currentLink.id });
    } else if (value === 'delete') {
      _confirm(
        t('promo_links_delete_confirm', {
          defaultValue: 'You are about to delete this link. Are you sure?',
        }),
        () => dispatch(promoLinksActions.deletePromoLink(currentLink.id)),
      );
    }

    setCurrentLinkId(0);
  };

  return (
    <>
      <Header showCloseButton>
        {t('promo_links_group_promo', { defaultValue: 'Referral links' })}
      </Header>
      <ScrollView
        className="PromoLinksList"
        footer={
          <Button
            // editLinkId: 0 перекрывает возможный «хвост» от прошлого редактирования:
            // showModal переносит текущие route.params в новый URL.
            onClick={() => showModal('create_promo_link', false, { editLinkId: 0 })}
            iconRight={<IcPlusCircle28 />}
          >
            {t('promo_links_create_new', { defaultValue: 'Create' })}
          </Button>
        }
      >
        {isLinksLoading ? (
          <Loader isWrapped />
        ) : links.length ? (
          <div className="PromoLinksList__links">
            {links.map((link) => (
              <div
                key={link.id}
                className="PromoLinksList__item"
                onClick={() => setCurrentLinkId(link.id)}
              >
                <div className="PromoLinksList__item__content">
                  <div className="PromoLinksList__item__description">{link.webUrl}</div>
                  <div className="PromoLinksList__item__head">
                    <div className="PromoLinksList__item__title">{link.name}</div>
                    <div className="PromoLinksList__item__visits">
                      {t('promo_links_visits', { defaultValue: 'Visits' })}:{' '}
                      {prettifyNumber(link.visits)}
                    </div>
                  </div>
                  {!!link.description && (
                    <div className="PromoLinksList__item__note">{link.description}</div>
                  )}
                </div>
                <div className="PromoLinksList__item__copy">
                  <IcEdit28 />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Placeholder
            icon={<IcLink28 />}
            iconSize={80}
            title={t('promo_links_placeholder_title', {
              defaultValue: 'No promo links yet',
            })}
          >
            {t('promo_links_placeholder_desc', {
              defaultValue:
                'Create a link to track where your traffic comes from and earn from the referral program',
            })}
          </Placeholder>
        )}
      </ScrollView>
      <ActionBottomSheet
        title={currentLink?.name ?? ''}
        description={currentLink?.webUrl}
        isOpened={!!currentLinkId}
        options={[
          {
            label: t('promo_links_action_copy', { defaultValue: 'Copy link' }),
            value: 'copy',
            icon: <IcCopy28 />,
          },
          ...(currentLink?.miniAppUrl
            ? [
                {
                  label: t('promo_links_action_copy_mini_app', {
                    defaultValue: 'Copy Telegram link',
                  }),
                  value: 'copy_mini_app',
                  icon: <IcCopy28 />,
                },
              ]
            : []),
          {
            label: t('promo_links_action_edit', { defaultValue: 'Edit' }),
            value: 'edit',
            icon: <IcEdit28 />,
          },
          {
            label: t('promo_links_action_delete', { defaultValue: 'Delete' }),
            value: 'delete',
            mode: 'alert' as const,
          },
        ]}
        onChange={handleActionChange}
        onClose={() => setCurrentLinkId(0)}
      />
    </>
  );
};
